// Runs every loop in `live.modulators` once per session-clock tick and hands the samples to sinks. Samples never reach Redux, the channel, history or a preset (plan A10).
import { getObjectInitial, parseLivePath, resolve, schemaForLivePath } from '~/lib/params'
import {
	clockReading,
	cyclesAtPosition,
	isPhaseLocked,
	ONE_SHOT_CYCLES,
	positionAt,
	positionOfValue,
	phaseOffset,
	valueAtPosition,
} from './evaluate'
import { lerpColor, sampleRamp } from '~/lib/fillInterpolation'
import { defOf, FFT, isDriven, isDriverKey } from './model'

export const BLEND_MS = 300
// The time constant of the band average an audio drive swings around.
const AVERAGE_S = 2

const EMPTY = Object.freeze({})

/** An audio drive's position 0…1 at `t`, from the band's `level` (null: input closed); `rt` carries its average and position between calls. It swings around `rest` by `gain` × the level's departure from its recent average, so a steady level sits at rest; it rises at once and releases with time constant `fall`. */
export const followLevel = (def, rt, level, t) => {
	const dt = rt.at != null ? (t - rt.at) / 1000 : 0
	rt.at = t
	const rest = def.rest ?? FFT.rest.initial
	let target = rest
	// A closed input or another band starts the average afresh.
	if (level == null || rt.band !== def.band) rt.mean = undefined
	rt.band = def.band
	if (level != null) {
		rt.mean = rt.mean === undefined ? level : rt.mean + (level - rt.mean) * (1 - Math.exp(-dt / AVERAGE_S))
		target = Math.min(1, Math.max(0, rest + (def.gain ?? FFT.gain.initial) * (level - rt.mean)))
	}
	const fall = def.fall ?? FFT.fall.initial
	const pos = rt.pos ?? target
	rt.pos = target >= pos || !fall ? target : target + (pos - target) * Math.exp(-dt / fall)
	return rt.pos
}

/** Two samples at `t`: numbers lerp, colours lerp per channel. */
export const mix = (a, b, t) => (typeof a === 'string' ? lerpColor(a, b, t) : a + (b - a) * t)

/** The defs that move their params, a shared drive's resolved per param; a `manual` def is skipped as if absent. */
const drivenOf = section => {
	if (!section) return EMPTY
	let out = null
	for (const path in section) {
		const def = section[path]
		if (!isDriverKey(path) && !def?.src && isDriven(def)) continue
		out ??= { ...section }
		const shared = def?.src && defOf(section, path)
		if (isDriven(shared)) out[path] = shared
		else delete out[path]
	}
	return out ?? section
}

/** The runtime a def's motion runs on: its driver's, so every param it drives moves as one; an inline loop's own. */
const motionKey = (path, def) => def.src ?? `@${path}`

const isClock = def => def.drive === 'bpm' || def.drive === 'time'

// What maps clock time to a position. Editing any of it keeps the playhead where it is (in/out: see `keepPlayhead`), and a phase edit moves the playhead by the change.
const shapeKey = def => `${def.drive}:${def.mode}:${def.duration}:${def.direction}`
const easeOutSine = u => Math.sin((u * Math.PI) / 2)

// Summed phase shifts drift; a hair below a pass boundary would read as the far end.
const onBoundary = c => {
	const n = Math.round(c)
	return Math.abs(c - n) < 1e-12 ? n : c
}

/** The number `path` holds in `live` — what a blend starts from or settles to. */
export const baseValue = (live, path) => {
	const p = parseLivePath(path)
	const prop = schemaForLivePath(path, live.objects)
	if (!p || !prop) return undefined
	if (p.scope === 'effect') return resolve(live[p.effect]?.[p.key], prop)
	if (p.scope === 'source') return resolve(live.source_settings?.[p.source]?.[p.key], prop)
	const obj = live.objects?.[p.id]
	if (!obj) return undefined
	return obj[p.key] ?? getObjectInitial(obj.type, p.key)
}

/** An object absent from `state` is invisible there: its opacity fades to or from 0, not to the other end's loop. */
const absentOpacity = (state, path) => {
	const p = parseLivePath(path)
	return p?.scope === 'object' && p.key === 'opacity' && state.objects && !state.objects[p.id] ? 0 : undefined
}

/** `audio`: `{ level(band, t), need(on) }` for the `fft` drive. */
export function createEngine({ getState, clock, sink, audio }) {
	// Motion per `motionKey`: origin, held cycles, position. The hand per path: touch and blend.
	const runtimes = new Map()
	const hands = new Map()
	// Each driven path's `motionKey` as of the last tick.
	let keys = new Map()
	let samples = new Map()
	const listeners = new Set()

	const freshRuntime = (def, t) => ({
		key: shapeKey(def),
		offset: phaseOffset(def),
		origin: clockReading(def, clock, t),
		held: null,
		range: [def.in, def.out],
	})

	const freshHand = () => ({ w: 1, blend: null, touch: undefined, anchor: undefined })

	const cyclesAt = (def, rt, t) => {
		const reading = clockReading(def, clock, t)
		const base = isPhaseLocked(def) ? reading : reading - rt.origin
		return base / def.duration + phaseOffset(def)
	}

	const advance = (def, rt, t) => {
		if (!def.run) {
			rt.held ??= rt.started ? cyclesAt(def, rt, t) : phaseOffset(def)
			return rt.held
		}
		// A held drive set playing starts a paused transport; one first seen running (a new runner adopting the session) leaves the transport as the session has it.
		if (rt.held != null) {
			clock.play?.(t)
			// A finished one-shot replays; anything else resumes where it was held. A locked loop rejoins the grid.
			if (rt.held >= ONE_SHOT_CYCLES[def.mode]) rt.origin = clockReading(def, clock, t)
			else if (!isPhaseLocked(def))
				rt.origin = clockReading(def, clock, t) - (rt.held - phaseOffset(def)) * def.duration
			rt.held = null
		}
		rt.started = true
		return cyclesAt(def, rt, t)
	}

	const weight = (hand, t) => {
		const b = hand.blend
		if (!b) return hand.w
		const u = Math.min(1, (t - b.t0) / BLEND_MS)
		hand.w = b.from + (b.to - b.from) * easeOutSine(u)
		if (u >= 1) {
			hand.blend = null
			if (hand.w === 1) hand.anchor = undefined
		}
		return hand.w
	}

	// A preset fade in flight (plan A8): the destination's loops and numbers, and the fade's own progress.
	let fade = null
	// Runtimes of loops only the destination has, started at the fade's start, adopted when it settles.
	const incoming = new Map()

	// Re-anchor `rt` so `def` puts the playhead at position `p`: a paused loop parks there, a free-running one continues from it. A beat-locked loop rejoins its grid (plan A3).
	const rehome = (def, rt, p, t) => {
		const c = cyclesAtPosition(def, p, rt.cycles ?? phaseOffset(def))
		if (!def.run || rt.held != null) rt.held = c
		else if (!isPhaseLocked(def)) rt.origin = clockReading(def, clock, t) - (c - phaseOffset(def)) * def.duration
		rt.cycles = c
	}

	// An in/out edit moves the playhead only when the new range excludes it, as a file's loop markers do. A driver's runtime is shared by params with ranges of their own, so only an inline loop's moves.
	const keepPlayhead = (def, rt, t) => {
		if (def.src) return
		const was = rt.range
		rt.range = [def.in, def.out]
		if (fade || !isClock(def) || !was || rt.pos == null || (was[0] === def.in && was[1] === def.out)) return
		const span = def.out - def.in
		const p = span > 0 ? Math.min(1, Math.max(0, (was[0] + (was[1] - was[0]) * rt.pos - def.in) / span)) : 0
		if (p === rt.pos) return
		rehome(def, rt, p, t)
		rt.pos = p
	}

	const runtimeIn = (map, key, def, t) => {
		let rt = map.get(key)
		if (!rt) {
			rt = freshRuntime(def, t)
			map.set(key, rt)
		} else if (rt.key !== shapeKey(def)) {
			rt.key = shapeKey(def)
			if (rt.pos != null) rehome(def, rt, rt.pos, t)
			rt.offset = phaseOffset(def)
		} else if (rt.offset !== phaseOffset(def)) {
			// A running loop reads the phase on every tick; a held one is shifted by the change.
			if (rt.held != null) rt.held = rt.cycles = onBoundary(rt.held + phaseOffset(def) - rt.offset)
			rt.offset = phaseOffset(def)
		}
		keepPlayhead(def, rt, t)
		return rt
	}

	let ticks = 0
	// The position of `rt`'s motion at `t`, computed once a tick however many params it drives.
	const positionNow = (def, rt, t) => {
		if (rt.tick === ticks) return rt.pos
		rt.tick = ticks
		if (def.drive === 'fft') return followLevel(def, rt, audio?.level(def.band ?? 'low', t), t)
		rt.cycles = advance(def, rt, t)
		rt.pos = positionAt(def, rt.cycles)
		return rt.pos
	}

	const loopValue = (def, rt, t) => valueAtPosition(def, positionNow(def, rt, t))

	// One end of a fade: the loop's sample, else that side's own number.
	const endValue = (def, map, path, state, t) =>
		def ? loopValue(def, runtimeIn(map, motionKey(path, def), def, t), t) : baseValue(state, path)

	// A path moving onto a motion nobody runs yet (sharing its drive, or detached from one) carries its playhead there.
	const carry = mods => {
		const now = new Map()
		for (const path in mods) {
			const key = motionKey(path, mods[path])
			const was = keys.get(path)
			if (was && was !== key && !runtimes.has(key) && runtimes.has(was))
				runtimes.set(key, { ...runtimes.get(was) })
			now.set(path, key)
		}
		keys = now
	}

	const tick = t => {
		ticks++
		const live = getState().live
		const mods = drivenOf(live.modulators)
		const toMods = drivenOf(fade?.to.modulators)
		audio?.need(
			Object.values(mods).some(d => d.drive === 'fft') || Object.values(toMods).some(d => d.drive === 'fft')
		)
		const next = new Map()
		carry(mods)
		// A destination loop on the same motion and shape as the source's keeps the source's runtime.
		const toKeys = new Set()
		const sharedKeys = new Set()
		for (const path in toMods) {
			const key = motionKey(path, toMods[path])
			toKeys.add(key)
			if (mods[path] && keys.get(path) === key && shapeKey(mods[path]) === shapeKey(toMods[path]))
				sharedKeys.add(key)
		}
		const used = new Set([...keys.values(), ...toKeys])
		for (const key of runtimes.keys()) if (!used.has(key)) runtimes.delete(key)
		for (const path of hands.keys()) if (!mods[path]) hands.delete(path)
		for (const key of incoming.keys()) if (!toKeys.has(key) || sharedKeys.has(key)) incoming.delete(key)
		for (const path of new Set([...Object.keys(mods), ...Object.keys(toMods)])) {
			const def = mods[path]
			const rt = def && runtimeIn(runtimes, motionKey(path, def), def, t)
			const hand = hands.get(path)
			if (hand?.touch !== undefined) {
				next.set(path, hand.touch)
				continue
			}
			if (fade) {
				// A loop on both ends keeps one runtime, so its phase runs on through the fade; a destination of another shape runs its own, else each end would re-anchor the shared one onto the other every tick and stall it.
				const toDef = toMods[path]
				const shared = toDef && sharedKeys.has(motionKey(path, toDef))
				const a = endValue(def, runtimes, path, live, t) ?? absentOpacity(live, path)
				const b =
					endValue(toDef, shared ? runtimes : incoming, path, fade.to, t) ?? absentOpacity(fade.to, path)
				const p = fade.progress()
				next.set(path, a === undefined ? b : b === undefined ? a : mix(a, b, p))
				continue
			}
			const value = loopValue(def, rt, t)
			const w = hand ? weight(hand, t) : 1
			// The w = 0 end: the hand's last value after a touch, else the param's own number.
			const from = w < 1 ? (hand.anchor ?? baseValue(live, path)) : undefined
			next.set(path, from === undefined ? value : mix(from, value, w))
		}
		const prev = samples
		samples = next
		sink?.(samples, prev, live)
		if (next.size || prev.size) for (const fn of listeners) fn(samples)
	}

	/** `path`'s def as it drives now, and the key its motion runs under; undefined while nothing drives it. */
	const drivenAt = path => {
		const def = defOf(getState().live.modulators, path)
		return isDriven(def) ? { def, key: motionKey(path, def) } : undefined
	}

	const positionOf = (path, t) => {
		const d = drivenAt(path)
		if (!d || !runtimes.has(d.key)) return undefined
		// A def edited since the last tick re-anchors first: a frame drawn in between would read the new shape on the old anchor.
		const rt = runtimeIn(runtimes, d.key, d.def, t)
		if (!isClock(d.def)) return rt.pos
		return positionAt(d.def, rt.held ?? cyclesAt(d.def, rt, t))
	}

	return {
		tick,
		/** A preset fade starts: `to` is its destination settings, `progress()` its 0…1 position. */
		beginFade(to, progress) {
			fade = { to: to ?? EMPTY, progress }
		},
		/** The fade settled or died. Landed, a destination loop with a runtime of its own keeps it; short of its end, the source's stay. */
		endFade() {
			if (fade?.progress() >= 1) for (const [key, rt] of incoming) runtimes.set(key, rt)
			incoming.clear()
			fade = null
			// Whichever end the fade left is the new baseline, never an in/out edit.
			for (const rt of runtimes.values()) rt.range = null
		},
		/** The latest sample per looped path. */
		samples: () => samples,
		sample: path => samples.get(path),
		/** The playhead's position along in→out, 0…1, for a surface to draw. */
		position: positionOf,
		/** The loop body's playhead in param units: the hand while touched, else the position mapped onto in→out — time (or level), never the envelope's value. */
		playhead(path, t) {
			const pos = positionOf(path, t)
			if (pos === undefined) return undefined
			const hand = hands.get(path)
			if (hand?.touch !== undefined) return hand.touchAt ?? hand.touch
			const { def } = drivenAt(path)
			return def.in + (def.out - def.in) * pos
		},
		/** A playing one-shot that has reached its end: it holds there until played again. Runtime only, so a preset's one-shot still plays on every load. */
		ended(path) {
			const d = drivenAt(path)
			const end = d && isClock(d.def) && ONE_SHOT_CYCLES[d.def.mode]
			return !!(end && d.def.run && runtimes.get(d.key)?.cycles >= end)
		},
		/** The def on `path` was replaced whole: its phase is absolute, so the playhead lands on it rather than shifting by the change. */
		restart(path, t = performance.now()) {
			const d = drivenAt(path)
			const rt = d && runtimes.get(d.key)
			if (!rt) return
			const { def } = d
			rt.key = shapeKey(def)
			rt.offset = phaseOffset(def)
			if (!isClock(def)) return
			if (!def.run || rt.held != null) rt.held = rt.cycles = phaseOffset(def)
			else if (!isPhaseLocked(def)) rt.origin = clockReading(def, clock, t)
		},
		/** Play a one-shot again from its start. */
		replay(path, t = performance.now()) {
			const d = drivenAt(path)
			const rt = d && runtimes.get(d.key)
			if (!rt) return
			rt.origin = clockReading(d.def, clock, t)
			rt.held = null
		},
		subscribe(fn) {
			listeners.add(fn)
			return () => listeners.delete(fn)
		},
		/** Park a paused loop's playhead at `value`'s place along in→out (a colour: its nearest place on the ramp); play resumes from there. */
		seek(path, value, t = performance.now()) {
			const d = drivenAt(path)
			const def = d?.def
			if (!def || !isClock(def) || def.run) return
			const rt = runtimeIn(runtimes, d.key, def, t)
			const span = def.out - def.in
			const p =
				typeof value === 'string'
					? positionOfValue(def, value)
					: span > 0
						? Math.min(1, Math.max(0, (value - def.in) / span))
						: 0
			rt.held = cyclesAtPosition(def, p, rt.held ?? phaseOffset(def))
			rt.cycles = rt.held
			rt.pos = p
		},
		/** The hand holds the param (plan A2): it outputs `value` until released. A colour's lane hands a position, output as the ramp there. */
		touch(path, value, t = performance.now()) {
			const d = drivenAt(path)
			if (!d) return
			const { def } = d
			runtimeIn(runtimes, d.key, def, t)
			if (!hands.has(path)) hands.set(path, freshHand())
			const hand = hands.get(path)
			hand.touch = def.stops && typeof value === 'number' ? sampleRamp(def.stops, value) : value
			// Where a colour's lane draws the hand.
			if (def.stops) hand.touchAt = typeof value === 'number' ? value : positionOfValue(def, value)
			hand.w = 0
			hand.blend = null
		},
		/** Let go: the loop blends back in at the clock's current phase. */
		release(path, t) {
			const hand = hands.get(path)
			if (!hand || hand.touch === undefined) return
			hand.anchor = hand.touch
			hand.touch = hand.touchAt = undefined
			hand.blend = { from: 0, to: 1, t0: t }
		},
	}
}
